import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
  inputDataChanged,
  conversionDirectionChanged,
  csvDelimiterChanged,
  jsonIndentationChanged,
  includeHeadersChanged,
  clearError,
  ConversionDirection,
  selectState,
} from "./CsvJsonConverterSlice";
import {
  Box,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Alert,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { CustomSelect } from "../../components/custom_select/CustomSelect";
import { CustomButton } from "../../components/custom_button/CustomButton";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import ClearIcon from "@mui/icons-material/Clear";
import { HomeCard } from "../../components/home_card/HomeCard";
import QrCodeGeneratorImage from "../../assets/QrCodeGenerator.webp";
import PasswordGeneratorImage from "../../assets/PasswordGenerator.webp";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function CsvJsonConverter() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const [copySuccess, setCopySuccess] = useState(false);
  const { t } = useTranslation();

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(state.outputData);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([state.outputData], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    
    const extension = state.conversionDirection === ConversionDirection.CSV_TO_JSON ? "json" : "csv";
    const filename = `converted_data.${extension}`;
    
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSwapDirection = () => {
    dispatch(conversionDirectionChanged(
      state.conversionDirection === ConversionDirection.CSV_TO_JSON 
        ? ConversionDirection.JSON_TO_CSV 
        : ConversionDirection.CSV_TO_JSON
    ));
  };

  const handleClearInput = () => {
    dispatch(inputDataChanged(""));
  };

  const getSampleData = () => {
    if (state.conversionDirection === ConversionDirection.CSV_TO_JSON) {
      return `name,age,city
John Doe,30,New York
Jane Smith,25,Los Angeles
Bob Johnson,35,Chicago`;
    } else {
      return `[
  {"name": "John Doe", "age": 30, "city": "New York"},
  {"name": "Jane Smith", "age": 25, "city": "Los Angeles"},
  {"name": "Bob Johnson", "age": 35, "city": "Chicago"}
]`;
    }
  };

  const loadSampleData = () => {
    dispatch(inputDataChanged(getSampleData()));
  };

  const renderErrorMessage = (error: string) => {
    if (error === "Conversion failed") return t("csvJsonConverter.errors.conversionFailed");
    if (error === "CSV data is empty") return t("csvJsonConverter.errors.csvEmpty");
    if (error === "No CSV lines found") return t("csvJsonConverter.errors.noCsvLines");
    if (error === "JSON data is empty") return t("csvJsonConverter.errors.jsonEmpty");
    if (error === "Invalid JSON format") return t("csvJsonConverter.errors.invalidJson");
    if (error === "JSON must be an array of objects") return t("csvJsonConverter.errors.jsonArrayRequired");
    if (error === "No valid object properties found in JSON array") return t("csvJsonConverter.errors.noJsonProps");
    return error;
  };

  return (
    <SideMenuPageContainer
      pageTitle={t("csvJsonConverter.title")}
      pageSeoTitle={t("csvJsonConverter.seoTitle")}
      pageDescription={t("csvJsonConverter.description")}
    >
      <Box sx={{ width: { xs: 300, sm: 350, md: 800, lg: 1000 } }}>
        <Grid2
          container
          spacing={2}
          sx={{ mt: 2 }}
          columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
        >
          <Grid2 xs={12}>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t("csvJsonConverter.mainHeading")}
            </Typography>
          </Grid2>

          {/* Conversion Direction and Options */}
          <Grid2 xs={12}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <CustomSelect
                label={t("csvJsonConverter.conversionDirection.label")}
                value={state.conversionDirection}
                onChange={(e) => {
                  dispatch(conversionDirectionChanged(e.target.value as ConversionDirection));
                }}
              >
                <MenuItem value={ConversionDirection.CSV_TO_JSON}>{t("csvJsonConverter.conversionDirection.csvToJson")}</MenuItem>
                <MenuItem value={ConversionDirection.JSON_TO_CSV}>{t("csvJsonConverter.conversionDirection.jsonToCsv")}</MenuItem>
              </CustomSelect>
              
              <Tooltip title={t("csvJsonConverter.conversionDirection.swap") as string}>
                <IconButton onClick={handleSwapDirection} color="secondary">
                  <SwapHorizIcon />
                </IconButton>
              </Tooltip>
              
              <Button 
                variant="outlined" 
                onClick={loadSampleData}
                size="small"
                sx={{ color: "#aedff2", borderColor: "#aedff2" }}
              >
                {t("csvJsonConverter.loadSample")}
              </Button>
            </Stack>
          </Grid2>

          {/* Configuration Options */}
          <Grid2 xs={12}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <TextField
                label={t("csvJsonConverter.options.csvDelimiter")}
                value={state.csvDelimiter}
                onChange={(e) => dispatch(csvDelimiterChanged(e.target.value))}
                sx={{ width: 120 }}
                inputProps={{ maxLength: 1 }}
              />
              
              {state.conversionDirection === ConversionDirection.CSV_TO_JSON && (
                <TextField
                  label={t("csvJsonConverter.options.jsonIndentation")}
                  type="number"
                  value={state.jsonIndentation}
                  onChange={(e) => dispatch(jsonIndentationChanged(Number(e.target.value)))}
                  inputProps={{ min: 0, max: 8 }}
                  sx={{ width: 140 }}
                />
              )}
              
              <FormControlLabel
                control={
                  <Switch
                    checked={state.includeHeaders}
                    onChange={(e) => dispatch(includeHeadersChanged(e.target.checked))}
                    color="primary"
                  />
                }
                label={t("csvJsonConverter.options.includeHeaders")}
              />
            </Stack>
          </Grid2>

          {/* Error Display */}
          {state.error && (
            <Grid2 xs={12}>
              <Alert 
                severity="error" 
                onClose={() => dispatch(clearError())}
                sx={{ mb: 2 }}
              >
                {renderErrorMessage(state.error)}
              </Alert>
            </Grid2>
          )}

          {/* Input Section */}
          <Grid2 xs={12} md={6}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h4">
                  {t("csvJsonConverter.input.title", { type: state.conversionDirection === ConversionDirection.CSV_TO_JSON ? "CSV" : "JSON" })}
                </Typography>
                <Tooltip title={t("csvJsonConverter.input.clear") as string}>
                  <IconButton onClick={handleClearInput} size="small" color="secondary">
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
              <TextField
                multiline
                rows={15}
                fullWidth
                variant="outlined"
                placeholder={t("csvJsonConverter.input.placeholder", { type: state.conversionDirection === ConversionDirection.CSV_TO_JSON ? "CSV" : "JSON" }) as string}
                value={state.inputData}
                onChange={(e) => dispatch(inputDataChanged(e.target.value))}
                sx={{ 
                  "& .MuiInputBase-input": { 
                    fontFamily: "monospace",
                    fontSize: "14px"
                  }
                }}
              />
            </Stack>
          </Grid2>

          {/* Output Section */}
          <Grid2 xs={12} md={6}>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h4">
                  {t("csvJsonConverter.output.title", { type: state.conversionDirection === ConversionDirection.CSV_TO_JSON ? "JSON" : "CSV" })}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Tooltip title={copySuccess ? t("csvJsonConverter.output.copied") as string : t("csvJsonConverter.output.copy") as string}>
                    <span>
                      <IconButton 
                        onClick={handleCopyToClipboard} 
                        disabled={!state.outputData}
                        size="small"
                        color={copySuccess ? "success" : "secondary"}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={t("csvJsonConverter.output.download") as string}>
                    <span>
                      <IconButton 
                        color="secondary"
                        onClick={handleDownload} 
                        disabled={!state.outputData}
                        size="small"
                      >
                        <DownloadIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
              <TextField
                multiline
                rows={15}
                fullWidth
                variant="outlined"
                value={state.outputData}
                InputProps={{
                    readOnly: true,
                  }}
                placeholder={t("csvJsonConverter.output.placeholder") as string}
                sx={{ 
                  "& .MuiInputBase-input": { 
                    fontFamily: "monospace",
                    fontSize: "14px"
                  }
                }}
              />
            </Stack>
          </Grid2>

          <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
            <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
          </Grid2>

          {/* SEO Content */}
          <Grid2 xs={12}>
          <AdSense
              adSlot={ADSENSE_SLOTS.IN_ARTICLE}
              adFormat="fluid"
              adLayoutKey="-fb+5w+4e-db+86"
              sx={{ mb: 3 }}
            />
            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("csvJsonConverter.content.freeHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("csvJsonConverter.content.freeDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("csvJsonConverter.content.flexibleHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("csvJsonConverter.content.flexibleDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("csvJsonConverter.content.privateHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("csvJsonConverter.content.privateDescription")}
            </Typography>

            <Typography variant="h3" sx={{ mb: 2 }}>
              {t("csvJsonConverter.content.developersHeading")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {t("csvJsonConverter.content.developersDescription")}
            </Typography>
          </Grid2>

          <Grid2 xs={12}>
            <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
              {t("csvJsonConverter.relatedTools")}
            </Typography>
          </Grid2>

          <Grid2 xs={6}>
            <HomeCard
              languageResource="qrCodeGenerator"
              link="/QrCodeGenerator"
              smallCard={true}
              image={QrCodeGeneratorImage}
            />
          </Grid2>
          <Grid2 xs={6}>
            <HomeCard
              languageResource="passwordGenerator"
              link="/PasswordGenerator"
              smallCard={true}
              image={PasswordGeneratorImage}
            />
          </Grid2>
        </Grid2>
      </Box>
    </SideMenuPageContainer>
  );
}
